import { useEffect } from 'react';

import { PartnerLookupFilters } from 'components/partner_lookup/components/PartnerLookupFilters';
import { PartnerLookupTable } from 'components/partner_lookup/components/PartnerLookupTable';
import { Partner, PartnerApi, PartnerSortField, PartnerTypes } from 'apis/PartnerApi';
import { PartnerLookupContext } from 'components/partner_lookup/context';
import { useListCache, useSuperState } from 'hooks';
import { SortDescriptor } from 'utils/dataQuery';
import { useIntl } from 'react-intl';

export type PartnerLookupFilterValues = {
    countryId: number;
    nameSearch: string;
    partnerType: number;
    sector: number;
    stateId: number;
    status: number;
};

const DEFAULT_FILTERS: PartnerLookupFilterValues = {
    countryId: 0,
    nameSearch: '',
    partnerType: PartnerTypes.TestingCenter,
    sector: 0,
    stateId: 0,
    status: -1
};

// Upper bound of rows pulled when advanced filters are active. The paged endpoint can only
// filter by name, so the full set is fetched and filtered/paginated on the client.
const MAX_LOOKUP_ROWS = 5000;

const hasAdvancedFilters = (f: PartnerLookupFilterValues) => f.sector > 0 || f.status !== -1 || f.countryId > 0 || f.stateId > 0;

export type PartnerLookupContextValue = {
    changeFilters: (updates: Partial<PartnerLookupFilterValues>) => void;
    clearFilters: () => void;
    disabled?: boolean;
    error: string;
    filters: PartnerLookupFilterValues;
    handlePageChange: (skip: number, take: number) => void;
    handleSelect: (partner: Partner) => void;
    handleSortChange: (sort: SortDescriptor[]) => void;
    loading: boolean;
    paginationSkip: number;
    paginationTake: number;
    partners: Partner[];
    partnerType: number;
    search: () => void;
    singleSearch: boolean;
    sortDir: string;
    sortField: PartnerSortField | '';
    totalCount: number;
}

export type PartnerLookupWidgetProps = {
    disabled?: boolean;
    onSelect: (partner: Partner) => void;
    partnerType?: number;
    singleSearch?: boolean;
    title?: string;
};

type PartnerLookupCache = {
    filters: PartnerLookupFilterValues;
    paginationSkip: number;
    paginationTake: number;
    sortDir: string;
    sortField: PartnerSortField | '';
};

export const PartnerLookupWidget = (props: PartnerLookupWidgetProps) => {

    const { disabled = false, onSelect, partnerType, singleSearch = false, title } = props;

    const { formatMessage } = useIntl();

    // Restore the previous view (filters, sort, pagination) so unmounting and remounting the
    // lookup returns to the same view, then refetch fresh partner rows for it.
    const partnerLookupCache = useListCache<PartnerLookupCache>(`partnerLookup:${partnerType ?? 'default'}`);
    const cachedLookup = partnerLookupCache.read();

    const [state, setState] = useSuperState({
        allPartners: [] as Partner[],
        error: null as string,
        filters: cachedLookup?.filters ?? ({
            ...DEFAULT_FILTERS,
            partnerType: partnerType || DEFAULT_FILTERS.partnerType
        }) as PartnerLookupFilterValues,
        loading: false,
        paginationSkip: cachedLookup?.paginationSkip ?? 0,
        paginationTake: cachedLookup?.paginationTake ?? 10,
        partners: [] as Partner[],
        sortDir: (cachedLookup?.sortDir ?? '') as string,
        sortField: (cachedLookup?.sortField ?? '') as PartnerSortField | '',
        totalCount: 0
    });
    const { allPartners, error, filters, loading, paginationSkip, paginationTake, partners, sortDir, sortField, totalCount } = state;

    const fetchPartners = async (skip = paginationSkip, take = paginationTake, sField = sortField, sDir = sortDir) => {

        const activeType = partnerType || filters.partnerType;

        if (!activeType || activeType === 0) return;

        setState({ error: '', loading: true });

        try {

            // Advanced filters (sector/status/country/state/name) aren't supported by the paged
            // endpoint, so pull the full server-sorted, name-searched set and filter + paginate
            // on the client. Otherwise filters would only ever match the current page.
            if (hasAdvancedFilters(filters)) {

                const result = await PartnerApi.GetAllPaged(activeType, 1, MAX_LOOKUP_ROWS, sField, sDir, filters.nameSearch);

                const items = result.Items.map(p => ({ ...p, Id: p.ID ?? p.Id }));
                const filtered = applyAdvancedFilters(items);
                const pageItems = filtered.slice(skip, skip + take);

                setState({
                    allPartners: filtered,
                    loading: false,
                    partners: pageItems,
                    totalCount: filtered.length
                });
                partnerLookupCache.write({
                    filters,
                    paginationSkip: skip,
                    paginationTake: take,
                    sortDir: sDir,
                    sortField: sField as PartnerSortField | ''
                });
                return;

            }

            const page = Math.floor(skip / take) + 1;
            const result = await PartnerApi.GetAllPaged(
                activeType,
                page,
                take,
                sField,
                sDir,
                filters.nameSearch
            );

            // Normalize Id field (API returns ID, GMetrixTable expects Id)
            const items = result.Items.map(p => ({ ...p, Id: p.ID ?? p.Id }));

            setState({
                allPartners: [],
                loading: false,
                partners: items,
                totalCount: result.TotalCount
            });
            partnerLookupCache.write({
                filters,
                paginationSkip: skip,
                paginationTake: take,
                sortDir: sDir,
                sortField: sField as PartnerSortField | ''
            });

        } catch (e) {

            setState({ error: e?.message ?? formatMessage({ id: 'failed_to_load_partners.40e8d84b', defaultMessage: 'Failed to load partners' }), loading: false });

        }

    };

    const applyAdvancedFilters = (items: Partner[]): Partner[] => {

        let result = items;

        if (filters.sector > 0) {

            result = result.filter(p => p.Sector === filters.sector);

        }

        if (filters.status !== -1) {

            result = result.filter(p => filters.status === 1 ? p.Active === true : p.Active === false
            );

        }

        if (filters.countryId > 0) {

            result = result.filter(p => p.Country?.Id === filters.countryId);

        }

        if (filters.stateId > 0) {

            result = result.filter(p => p.State?.Id === filters.stateId);

        }

        return result;

    };

    const search = () => {

        setState({ paginationSkip: 0 });
        fetchPartners(0, paginationTake, sortField, sortDir);

    };

    const clearFilters = () => setState({
        filters: {
            ...DEFAULT_FILTERS,
            partnerType: partnerType || DEFAULT_FILTERS.partnerType
        }
    });

    const changeFilters = (updates: Partial<PartnerLookupFilterValues>) => setState({
        filters: {
            ...filters,
            ...updates
        }
    });

    const handlePageChange = (skip: number, take: number) => {

        // With advanced filters active the full filtered set lives on the client, so page
        // through it locally instead of refetching a server page.
        if (hasAdvancedFilters(filters) && allPartners.length > 0) {

            setState({ paginationSkip: skip, paginationTake: take, partners: allPartners.slice(skip, skip + take) });
            return;

        }

        setState({ paginationSkip: skip, paginationTake: take });
        fetchPartners(skip, take, sortField, sortDir);

    };

    const handleSortChange = (sort: SortDescriptor[]) => {

        const newSortField = (sort[0]?.field ?? '') as PartnerSortField | '';
        const newSortDir = sort[0]?.dir ?? '';

        setState({ sortDir: newSortDir, sortField: newSortField });
        fetchPartners(paginationSkip, paginationTake, newSortField, newSortDir);

    };

    useEffect(() => {

        if (filters.partnerType > 0) {

            fetchPartners();

        }

    }, []);

    const context: PartnerLookupContextValue = {
        changeFilters,
        clearFilters,
        disabled: disabled || loading,
        error,
        filters,
        handlePageChange,
        handleSelect: onSelect,
        handleSortChange,
        loading,
        paginationSkip,
        paginationTake,
        partners,
        partnerType,
        search,
        singleSearch,
        sortDir,
        sortField,
        totalCount
    };

    return (
        <PartnerLookupContext.Provider value={context}>
            <div className={'partner-lookup-content d-flex flex-column gap-3'}>
                {title && (
                    <h2 className="mb-3">
                        {title}
                    </h2>
                )}

                <PartnerLookupFilters />

                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}

                <PartnerLookupTable />
            </div>
        </PartnerLookupContext.Provider>
    );

};
